/**
 * Feature flags for contract-in-flight work (Coordinator directive,
 * Phase 2 kickoff): surfaces whose backend (Session H) isn't deployed yet
 * are built against the local dev stub and shipped default-OFF. Flags are
 * build-time (CRA env), so production stays off until the contract is live
 * AND the flag is deliberately enabled in a gated deploy.
 */

export const FEATURES = {
  // The client hub's Activity tab (ROLE-DESIGN Part 5). OFF → a designed
  // empty state explains the feed is coming; ON (dev stub only, for now) →
  // fetches the provisional endpoint in API.getActivitiesForClient.
  activityFeed: process.env.REACT_APP_FEATURE_ACTIVITY === "1",

  // Documents-by-owner reads (pre-agreed contract, Coordinator 2026-07-11:
  // GET /api/documents/owner/:ownerModel/:ownerRef). OFF → the hub's
  // Documents tab falls back to /group over policy/client doc-id arrays
  // (app-created docs only) with an "imported docs pending" notice.
  documentsByOwner: process.env.REACT_APP_FEATURE_DOCS_BY_OWNER === "1",

  // Nudges (ROLE-DESIGN §3b, E §2): list + respond contract PROPOSED to the
  // Coordinator, not yet ratified. OFF → the referral home's nudge section
  // is absent (E: absent, not empty) and Today has no reach-out queue.
  nudges: process.env.REACT_APP_FEATURE_NUDGES === "1",

  // Pipeline stage WRITES (drag/move + lost-reason). Reads need no flag —
  // pipelineStage is additive on the existing scoped GET /api/prospects.
  pipelineWrites: process.env.REACT_APP_FEATURE_PIPELINE_WRITES === "1",

  // SR lifecycle (status enum/assignee/replies, H Tranche 3). OFF → the
  // legacy completed boolean maps to Open/Closed only; no claim/assign.
  srLifecycle: process.env.REACT_APP_FEATURE_SR_LIFECYCLE === "1",

  // Commissions page (Phase 3, R10 / RUNBOOK §3.11). computed-v1 — no new
  // backend; derives split lines + monthly rollup client-side from the scoped
  // A/H/U policy records. OFF → /app/commissions renders the designed
  // placeholder (referral tier no longer bounced to legacy — QA M-2 fix in
  // PlaceholderPage); ON → the real tier-forked report page.
  commissions: process.env.REACT_APP_FEATURE_COMMISSIONS === "1",

  // --- Phase 3 pinned small contracts (Session I complete; each default-OFF
  //     until its gated flag flip). `retention` is defined below (Unit 2). ---

  // Policy inactivate/reactivate (§10; Session I T4). Staff-only "Mark
  // inactive"/"Reactivate" on the policy drawer → PUT /api/{lob}/delete|reactivate/:id.
  // OFF → policies still render their Active/Inactive badge; no status action.
  policyInactivate: process.env.REACT_APP_FEATURE_POLICY_INACTIVATE === "1",

  // Reach-out "Mark contacted" (W12; PATCH /api/nudges/:id/contacted). OFF →
  // the Today reach-out queue stays display-only (its shipped behavior).
  nudgeContacted: process.env.REACT_APP_FEATURE_NUDGE_CONTACTED === "1",

  // Prospect → existing-client link (POST /api/prospects/:id/link-client).
  // Staff-only migrated-prospect sync. OFF → no link affordance.
  linkClient: process.env.REACT_APP_FEATURE_LINK_CLIENT === "1",

  // SR type + urgency as REAL fields (§10.3/§10.6). OFF → the form keeps the
  // legacy category select + urgency-as-message-prefix.
  srFields: process.env.REACT_APP_FEATURE_SR_FIELDS === "1",

  // Quote proposal-PDF download slot (D6; docKind:"proposal"). OFF → no slot
  // (renders only when a proposal doc with a file actually exists anyway).
  proposalDocs: process.env.REACT_APP_FEATURE_PROPOSAL_DOCS === "1",

  // Retention cases (Phase 3, ROLE-DESIGN Part 10.2/10.6). Client-initiated
  // re-quotes tracked on the EXISTING Prospect machinery via the additive
  // caseType:'retention' + clientRef fields (reads ride the scoped
  // GET /api/prospects; create reuses the existing prospect-create route —
  // Coordinator 2026-07-12). OFF → no caseType badge/filter, no refer
  // "already a client → retention case" path, no hub open-cases strip.
  retention: process.env.REACT_APP_FEATURE_RETENTION === "1",

  // --- Custodian unit (§10.7; Session L backend). Each default-OFF. ---

  // The Data-Fix queue (/app/datafixes, §10.7b) against the pinned T1 contract
  // (GET /api/custodian/queue + /count, PATCH /findings/:id/resolve). OFF → no
  // nav item, no route, no Today count card (net-new surface, no placeholder).
  dataFixes: process.env.REACT_APP_FEATURE_DATA_FIXES === "1",

  // "Fawn's Fix-its" — the staff↔Sentinel bar (§10.7d). Geometry/states ship
  // now; the POST fix-it + receipts read wire to L's T3 contract. OFF → the
  // bar never renders (staff-only even when on).
  fawnBar: process.env.REACT_APP_FEATURE_FAWN_BAR === "1",

  // Evan's standing pre-launch rule (expanded 2026-07-11): NO emails to any
  // client or agent until the app is fully live — evan@fawncircle.com is the
  // only permitted recipient. Firebase-sent password resets bypass the
  // backend MAIL_ALLOWLIST, so THIS FLAG is the enforcement point for them.
  // OFF → the forgot-password flow sends nothing and shows honest
  // contact-the-agency copy (evan@ himself excepted, for testing). Part of
  // the deploy-gate env checklist alongside DIGEST_ENABLED/MAIL_ALLOWLIST.
  emailsLive: process.env.REACT_APP_EMAILS_LIVE === "1"
};
// decUpload flag removed 2026-07-11: POST /api/documents/prospect/:id shipped
// (H Tranche 3) and the v1 binary path is ruled (browser → Firebase Storage,
// metadata POST after) — the dropzone is now permanent on the refer form.
